import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import UpgradeBanner from '../components/UpgradeBanner';
import Tooltip from '../components/Tooltip';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import EmptyState from '../components/EmptyState';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  Archive,
  ArrowCounterClockwise,
  ArrowRight,
  Calendar,
  CircleNotch,
  Clock,
  DotsThreeVertical,
  Eye,
  FileText,
  Funnel,
  Lightning,
  MagnifyingGlass,
  MapTrifold,
  PencilSimple,
  Plus,
  Pulse,
  Trash,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';
const ProcessLibrary = () => {
  const navigate = useNavigate();
  const { userPlan, planLimits } = usePlan();
  const { activeOrganization, loading: orgLoading } = useOrganization();
  const [processes, setProcesses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenuFor, setShowMenuFor] = useState(null);
  const [analyzing, setAnalyzing] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const menuRef = useRef(null);
  const [filters, setFilters] = useState({
    department: 'all',
    search: '',
    status: 'active' // 'active' or 'archived'
  });

  useEffect(() => {
    if (!activeOrganization || orgLoading) return;
    fetchDepartments();
    fetchProcesses();
    fetchStats();
  }, [activeOrganization, orgLoading]);

  useEffect(() => {
    if (!activeOrganization || orgLoading) return;
    fetchProcesses();
  }, [filters, activeOrganization]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenuFor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/account/stats`,
        {},
        activeOrganization?.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/departments`,
        {},
        activeOrganization?.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchProcesses = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.department !== 'all') params.append('department_id', filters.department);
      if (filters.search) params.append('search', filters.search);
      // When viewing archived, include them; when viewing active, exclude them
      if (filters.status === 'archived') {
        params.append('include_archived', 'true');
      }

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes?${params}`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        let data = await response.json();
        // Funnel based on archived status on client side
        if (filters.status === 'archived') {
          data = data.filter(p => p.is_archived === true);
        } else {
          data = data.filter(p => p.is_archived !== true);
        }
        setProcesses(data);
      }
    } catch (error) {
      console.error('Error fetching processes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveProcess = async (processId, processName) => {
    if (!window.confirm(`Archive "${processName}"? It will be hidden from your list but not permanently deleted.`)) {
      return;
    }

    setProcessingId(processId);
    setShowMenuFor(null);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${processId}/archive`,
        { method: 'POST' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        // Remove from local state
        setProcesses(prev => prev.filter(p => p.id !== processId));
      } else {
        alert('Failed to archive process');
      }
    } catch (error) {
      console.error('Error archiving process:', error);
      alert('Failed to archive process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRestoreProcess = async (processId, processName) => {
    setProcessingId(processId);
    setShowMenuFor(null);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${processId}/restore`,
        { method: 'POST' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        // Remove from local state (it will appear in active list)
        setProcesses(prev => prev.filter(p => p.id !== processId));
      } else {
        alert('Failed to restore process');
      }
    } catch (error) {
      console.error('Error restoring process:', error);
      alert('Failed to restore process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteProcess = async (processId, processName) => {
    if (!window.confirm(`Permanently delete "${processName}"? This action cannot be undone.`)) {
      return;
    }

    setProcessingId(processId);
    setShowMenuFor(null);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${processId}`,
        { method: 'DELETE' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        // Remove from local state
        setProcesses(prev => prev.filter(p => p.id !== processId));
      } else {
        alert('Failed to delete process');
      }
    } catch (error) {
      console.error('Error deleting process:', error);
      alert('Failed to delete process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAnalyzeProcess = async (processId) => {
    setAnalyzing({ ...analyzing, [processId]: true });
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/org/processes/${processId}/analyze`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        // Navigate to process detail to see results
        navigate(`/processes/${processId}`);
      } else {
        alert('Failed to analyze process. Please try again.');
      }
    } catch (error) {
      console.error('Error analyzing process:', error);
      alert('Failed to analyze process. Please try again.');
    } finally {
      setAnalyzing({ ...analyzing, [processId]: false });
    }
  };

  if (orgLoading || !activeOrganization || loading) {
    return (
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
        </div>
    );
  }

  return (
      <div className="max-w-7xl mx-auto">
          {/* Upgrade Banners */}
          {userPlan === 'starter' && (
            <UpgradeBanner targetPlan="pro" context="processes" />
          )}
          {userPlan === 'pro' && (
            <UpgradeBanner targetPlan="partner_pro" context="processes" />
          )}

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  Process <span className="text-[#6366F1]">Library</span>
                </h1>
                <p className="text-[#A0AEC0]">
                  MapTrifold, analyze, and optimize your business processes with AI
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/processes/new')}
                  className="rounded-full bg-[#6366F1] hover:bg-[#5558E6] transition-colors"
                >
                  <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                    <Plus size={20} />
                    New Process
                  </span>
                </button>

                <button
                  onClick={() => navigate('/process-templates')}
                  className="glass rounded-full px-6 py-3 flex items-center gap-2 text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                >
                  <FileText size={20} />
                  Templates
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-5 gap-6 mb-8">
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Total Processes</p>
                  <TrendUp size={20} className="text-[#14F195]" />
                </div>
                <p className="text-3xl font-bold text-white">{processes.length}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="text-[#A0AEC0] text-sm">AI Readiness</p>
                    <Tooltip text="AI Readiness is a combined score based on process clarity, data availability, workflow structure, and automation feasibility. Higher scores indicate processes that are easier and faster to automate.">
                      <div className="text-[#A0AEC0] cursor-help">ⓘ</div>
                    </Tooltip>
                  </div>
                  <Pulse size={20} className="text-[#8B5CF6]" />
                </div>
                <Tooltip text="AI Readiness is a combined score based on process clarity, data availability, workflow structure, and automation feasibility. Higher scores indicate processes that are easier and faster to automate.">
                  <div className="flex items-baseline gap-2 cursor-help mb-3">
                    <p className="text-3xl font-bold text-white">
                      {stats?.last_readiness_score ? `${stats.last_readiness_score}%` : '–'}
                    </p>
                    {stats?.last_readiness && stats.last_readiness !== '–' && (
                      <span className="text-lg font-semibold text-[#8B5CF6]">
                        {stats.last_readiness}
                      </span>
                    )}
                  </div>
                </Tooltip>
                {/* Progress Bar */}
                <div className="w-full h-2 bg-[#1A1B23] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#6366F1]"
                    style={{ width: `${stats?.last_readiness_score || 0}%` }}
                  ></div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Departments</p>
                  <UsersThree size={20} className="text-[#6366F1]" />
                </div>
                <p className="text-3xl font-bold text-white">{departments.length}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Analyzed</p>
                  <Lightning size={20} className="text-[#EC4899]" />
                </div>
                <p className="text-3xl font-bold text-white">
                  {processes.filter(p => p.last_analyzed_at).length}
                </p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Total Steps</p>
                  <Clock size={20} className="text-[#F59E0B]" />
                </div>
                <p className="text-3xl font-bold text-white">
                  {processes.reduce((sum, p) => sum + (p.step_count || 0), 0)}
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center">
              <div className="flex-1 relative">
                <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#A0AEC0]" />
                <input
                  type="text"
                  placeholder="Search processes..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                />
              </div>

              {/* Status Funnel Buttons */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setFilters({ ...filters, status: 'active' })}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    filters.status === 'active'
                      ? 'bg-[#6366F1] text-white shadow-sm'
                      : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilters({ ...filters, status: 'archived' })}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                    filters.status === 'archived'
                      ? 'bg-[#6366F1] text-white shadow-sm'
                      : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <Archive size={14} />
                  Archived
                </button>
              </div>

              <select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                className="px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] cursor-pointer"
                style={{ colorScheme: 'dark' }}
              >
                <option value="all" className="bg-[#1a1a2e] text-white">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id} className="bg-[#1a1a2e] text-white">{dept.name}</option>
                ))}
              </select>
            </div>
          </motion.div>

          {/* Process List */}
          {processes.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-3xl"
            >
              <EmptyState
                icon={MapTrifold}
                title="No processes mapped yet"
                description="Start mapping your workflows visually."
                buttonText="Create New Process"
                onButtonClick={() => navigate('/processes/new')}
                gradient=""
              />
            </motion.div>
          ) : (
            <div className="space-y-1">
              {processes.map((process, index) => {
                const dept = departments.find(d => d.id === process.department_id);
                return (
                  <motion.div
                    key={process.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="group relative flex items-center gap-4 px-4 py-3 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.03)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] hover:border-[rgba(99,102,241,0.2)] transition-all cursor-pointer"
                    onClick={() => navigate(`/processes/${process.id}`)}
                  >
                    {/* Icon */}
                    <div className="w-10 h-10 rounded-lg bg-[#6366F1] flex items-center justify-center flex-shrink-0">
                      <MapTrifold size={18} className="text-white" />
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-white truncate">{process.name}</h3>
                        
                        {/* Badges */}
                        <div className="flex items-center gap-1.5">
                          {/* Step Count Badge */}
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20 rounded">
                            {process.step_count || 0} steps
                          </span>
                          
                          {/* Analyzed Badge */}
                          {process.last_analyzed_at && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#EC4899]/10 text-[#EC4899] border border-[#EC4899]/20 rounded">
                              Analyzed
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Metadata row */}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {dept && (
                          <span className="flex items-center gap-1">
                            <UsersThree size={11} />
                            {dept.name}
                          </span>
                        )}
                        {(process.owner_name || process.owner_role) && (
                          <span className="truncate max-w-[150px]">
                            {process.owner_name && process.owner_role 
                              ? `${process.owner_name} / ${process.owner_role}`
                              : process.owner_name || process.owner_role
                            }
                          </span>
                        )}
                        {process.volume_per_period && (
                          <span className="flex items-center gap-1">
                            <Pulse size={11} />
                            {process.volume_per_period}/{process.frequency || 'period'}
                          </span>
                        )}
                        {process.avg_cycle_time_minutes && (
                          <span className="flex items-center gap-1">
                            <Clock size={11} />
                            {process.avg_cycle_time_minutes} min
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Right side - Last analyzed date */}
                    <div className="hidden md:flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />
                        {process.last_analyzed_at 
                          ? new Date(process.last_analyzed_at).toLocaleDateString()
                          : 'Not analyzed'
                        }
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      {/* Analyze Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnalyzeProcess(process.id);
                        }}
                        disabled={analyzing[process.id]}
                        className="px-3 py-1.5 bg-[#EC4899]/10 text-[#EC4899] border border-[#EC4899]/20 rounded-md text-xs font-medium hover:bg-[#EC4899]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        title="Run Opportunity Analysis"
                      >
                        {analyzing[process.id] ? (
                          <CircleNotch size={12} className="animate-spin" />
                        ) : (
                          <Lightning size={12} />
                        )}
                        Analyze
                      </button>
                      
                      {/* View Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/processes/${process.id}`);
                        }}
                        className="px-3 py-1.5 bg-[rgba(255,255,255,0.05)] text-gray-400 border border-[rgba(255,255,255,0.1)] rounded-md text-xs font-medium hover:bg-[rgba(255,255,255,0.1)] transition-colors flex items-center gap-1.5"
                        title="View Details"
                      >
                        <Eye size={12} />
                        View
                      </button>
                      
                      {/* More Actions Menu */}
                      <div className="relative" ref={showMenuFor === process.id ? menuRef : null}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenuFor(showMenuFor === process.id ? null : process.id);
                          }}
                          disabled={processingId === process.id}
                          className="p-1.5 hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors disabled:opacity-50"
                          title="More actions"
                        >
                          {processingId === process.id ? (
                            <CircleNotch size={14} className="text-gray-400 animate-spin" />
                          ) : (
                            <DotsThreeVertical size={14} className="text-gray-400" />
                          )}
                        </button>
                        
                        {/* Dropdown Menu */}
                        <AnimatePresence>
                          {showMenuFor === process.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -5 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -5 }}
                              transition={{ duration: 0.1 }}
                              className="absolute right-0 top-full mt-2 z-50 w-48 bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-xl shadow-xl py-1 overflow-hidden"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* PencilSimple */}
                              <button
                                onClick={() => {
                                  navigate(`/processes/${process.id}/edit`);
                                  setShowMenuFor(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.05)] transition-all"
                              >
                                <PencilSimple size={14} />
                                PencilSimple Process
                              </button>
                              
                              <div className="border-t border-[rgba(255,255,255,0.1)] my-1"></div>
                              
                              {/* Archive or Restore based on status */}
                              {filters.status === 'archived' ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRestoreProcess(process.id, process.name);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-green-400 hover:bg-green-500/10 transition-all"
                                >
                                  <ArrowCounterClockwise size={14} />
                                  Restore Process
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleArchiveProcess(process.id, process.name);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.05)] transition-all"
                                >
                                  <Archive size={14} />
                                  Archive Process
                                </button>
                              )}
                              
                              <div className="border-t border-[rgba(255,255,255,0.1)] my-1"></div>
                              
                              {/* Delete Permanently */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProcess(process.id, process.name);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-all"
                              >
                                <Trash size={14} />
                                Delete Permanently
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
  );
};

export default ProcessLibrary;
